import Phaser from 'phaser';
import { COLORS, COLORS_HEX, FONTS } from '@/ui/theme';
import { AudioManager } from '@/utils/AudioManager';
import { KioskWatchdog } from '@/utils/KioskWatchdog';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { GamepadNav } from '@/utils/GamepadNav';
import { HEADER_CARD_GAP } from '@/config/constants';

/**
 * MechanicsTutorialModal — first-play "1-2-3" controls card.
 *
 * Shown once per new game (after the player enters their name in
 * NameEntryScene), before the stage's educational intro card. Three
 * yellow bubble buttons number the steps; one cyan LET'S PLAY CTA
 * dismisses the modal and continues the intro flow.
 *
 *   ┌────────────────────────────────────────────────┐
 *   │             HELMEPA                            │
 *   │       POSEIDON CHALLENGE                       │
 *   │                                                │
 *   │     ① Pop every    ② Move with    ③ Fire with │
 *   │       bubble         arrows         SPACE      │
 *   │                                                │
 *   │              [ LET'S PLAY ]                    │
 *   └────────────────────────────────────────────────┘
 */
export class MechanicsTutorialModal extends Phaser.GameObjects.Container {
  private readonly overlay: Phaser.GameObjects.Rectangle;
  private readonly panel: Phaser.GameObjects.Graphics;
  private readonly headerGroup: Phaser.GameObjects.Container;
  private readonly stepsGroup: Phaser.GameObjects.Container;
  private readonly ctaButton: Phaser.GameObjects.Container;
  private readonly onDismiss: () => void;
  private dismissed = false;
  private nav?: GamepadNav;
  private padTick?: () => void;

  private static readonly STEPS: Array<{ n: string; lines: string[] }> = [
    { n: '1', lines: ['Pop every', 'bubble'] },
    { n: '2', lines: ['Move left and right', 'with the joystick'] },
    { n: '3', lines: ['Fire with A', 'and B buttons'] },
  ];

  constructor(scene: Phaser.Scene, onDismiss: () => void, opts?: { ctaLabel?: string }) {
    super(scene, 0, 0);
    scene.add.existing(this);
    this.setDepth(10_000);
    this.setScrollFactor(0);
    this.onDismiss = onDismiss;

    const cx = 960;
    const pw = 1140;
    const ph = 540;
    const panelLeft = cx - pw / 2;
    const panelRadius = 32;

    // ── very light scrim (stage bg shows through clearly) ──────────────
    this.overlay = scene.add.rectangle(0, 0, 1920, 1080, 0x020b18, 0.28)
      .setOrigin(0, 0)
      .setInteractive();
    this.add(this.overlay);

    // ── header (HELMEPA + smaller POSEIDON CHALLENGE) ─────────────────
    this.headerGroup = scene.add.container(0, 0);
    this.add(this.headerGroup);
    if (scene.textures.exists('helmepa_logo')) {
      const helmepa = scene.add.image(cx, 88, 'helmepa_logo').setOrigin(0.5);
      helmepa.setScale(162 / helmepa.width);
      helmepa.setAlpha(0.95);
      this.headerGroup.add(helmepa);
    }
    let titleBottom = 268.6; // fallback if title texture is missing
    if (scene.textures.exists('title_logo_v4')) {
      const title = scene.add.image(cx, 200, 'title_logo_v4').setOrigin(0.5);
      title.setScale(378 / title.width);
      this.headerGroup.add(title);
      titleBottom = title.y + title.displayHeight / 2;
    }

    // ── card panel ────────────────────────────────────────────────────
    const panelTop = Math.round(titleBottom + HEADER_CARD_GAP);
    this.panel = scene.add.graphics();
    this.drawPanel(this.panel, panelLeft, panelTop, pw, ph, panelRadius);
    this.add(this.panel);

    // ── three step bubbles + labels ───────────────────────────────────
    this.stepsGroup = scene.add.container(0, 0);
    this.add(this.stepsGroup);

    const stepCount = MechanicsTutorialModal.STEPS.length;
    const colW = pw / stepCount;
    const stepCy = panelTop + 130;
    MechanicsTutorialModal.STEPS.forEach((step, i) => {
      const colCx = panelLeft + colW * (i + 0.5);

      // Yellow bubble — reuses the menu's HIGH SCORES texture for visual
      // consistency. Falls back to a code-drawn yellow circle.
      const bubbleD = 120;
      if (scene.textures.exists('menu_btn_yellow_bubble')) {
        const bubble = scene.add.image(colCx, stepCy, 'menu_btn_yellow_bubble').setOrigin(0.5);
        bubble.setDisplaySize(bubbleD, bubbleD);
        this.stepsGroup.add(bubble);
      } else {
        const fallback = scene.add.graphics();
        fallback.fillStyle(0xffd34a, 1).fillCircle(colCx, stepCy, bubbleD / 2);
        fallback.lineStyle(4, 0xc28a18, 1).strokeCircle(colCx, stepCy, bubbleD / 2);
        this.stepsGroup.add(fallback);
      }

      // Big number on the bubble — Skranji Bold (chunky display face) per
      // the menu's CONTROLS / win-screen typography family.
      const num = scene.add.text(colCx, stepCy + 6, step.n, {
        fontFamily: FONTS.fancy,
        fontSize: '78px',
        color: '#3a2a00',
        stroke: '#fff5d0',
        strokeThickness: 2,
      }).setOrigin(0.5);
      this.stepsGroup.add(num);

      // Label lines under the bubble
      step.lines.forEach((line, li) => {
        const txt = scene.add.text(colCx, stepCy + bubbleD / 2 + 36 + li * 44, line, {
          fontFamily: FONTS.displayHeavy,
          fontSize: '32px',
          color: COLORS_HEX.textPrimary,
          stroke: COLORS_HEX.midnightDepth,
          strokeThickness: 4,
          shadow: { offsetX: 0, offsetY: 2, color: COLORS_HEX.midnightDepth, blur: 4, fill: true },
        }).setOrigin(0.5).setLetterSpacing(1);
        this.stepsGroup.add(txt);
      });
    });

    // ── CTA: same cyan rectangular button as PLAY on the menu ────────
    const ctaCx = cx;
    const ctaCy = panelTop + ph - 90;
    this.ctaButton = createPillButton(scene, {
      x: ctaCx,
      y: ctaCy,
      label: opts?.ctaLabel ?? "LET'S PLAY",
      variant: 'primary',
      fontSize: 44,
      onClick: () => this.dismiss(),
    });
    this.add(this.ctaButton);

    this.setAlpha(0);
    (this.panel as any).setScale?.(0.96);

    // Gamepad: any face button or Start dismisses the card.
    this.nav = new GamepadNav(scene, {
      onConfirm: () => this.dismiss(),
      onCancel:  () => this.dismiss(),
      onPause:   () => this.dismiss(),
    });

    // Selection halo on the lone CTA — visible whenever a gamepad is
    // connected, since any face button dismisses and the joystick is
    // effectively "on" the only target. Hidden for mouse / keyboard.
    const haloGraphics = scene.add.graphics();
    this.add(haloGraphics);
    this.bringToTop(this.ctaButton);
    this.padTick = () => {
      this.nav?.update();
      if (this.nav?.isPadConnected()) {
        drawSelectionHalo(haloGraphics, {
          cx: this.ctaButton.x,
          cy: this.ctaButton.y,
          w: ((this.ctaButton as any).pillWidth ?? 0) * this.ctaButton.scaleX,
          h: ((this.ctaButton as any).pillHeight ?? 0) * this.ctaButton.scaleY,
          rx: ((this.ctaButton as any).pillCapRx ?? 0) * this.ctaButton.scaleX,
          ry: ((this.ctaButton as any).pillCapRy ?? 0) * this.ctaButton.scaleY,
        }, { gap: 14 });
      } else {
        haloGraphics.clear();
      }
    };
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.padTick);
  }

  show(): void {
    // Mark a blocking dialog open for the kiosk idle watchdog; release it when
    // this object is destroyed (covers both normal dismiss and scene shutdown,
    // exactly once, so the counter can't leak).
    KioskWatchdog.instance.pushModal();
    this.once(Phaser.GameObjects.Events.DESTROY, () => KioskWatchdog.instance.popModal());
    this.scene.tweens.add({ targets: this, alpha: 1, duration: 280, ease: 'Power2' });
    this.scene.tweens.add({
      targets: [this.panel],
      scaleX: 1, scaleY: 1,
      duration: 360, ease: 'Back.easeOut',
    });
    AudioManager.getInstance().playSFX(this.scene, 'sfx_whoosh_reveal', { volume: 0.45 });
  }

  // ── visual helpers (mirror EducationIntroModal so the two cards feel
  //    like they belong to the same family) ───────────────────────────
  private drawPanel(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, r: number): void {
    // Translucent navy fill + single aqua hairline — matches the
    // EducationIntroModal so the two cards read as one family.
    g.fillStyle(COLORS.deepOcean, 0.55);
    g.fillRoundedRect(x, y, w, h, r);
    g.lineStyle(2, COLORS.aqua, 0.70);
    g.strokeRoundedRect(x, y, w, h, r);
  }

  private dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    if (this.padTick) {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.padTick);
      this.padTick = undefined;
    }
    this.nav?.destroy();
    this.nav = undefined;
    AudioManager.getInstance().playSFX(this.scene, 'sfx_iris_open', { volume: 0.5 });
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 220,
      ease: 'Power2',
      onComplete: () => {
        this.onDismiss();
        this.destroy();
      },
    });
  }
}
